import { useEffect, useMemo, useState } from 'react';
import {
  BODY_TYPE_LABEL,
  ORDER_STATUSES,
  ORDER_STATUS_LABEL,
  type CargoListItem,
  type DispatchBoard,
  type DispatchQuery,
  type DispatchRow,
  type OrderStatus,
} from 'shared';
import { api } from '../api';
import Icon from '../Icon';
import { alert, haptic } from '../telegram';
import { useLiveRefresh, useRealtime } from '../realtime';

/**
 * Диспетчерская площадки.
 *
 * Экран техподдержки: все рейсы, обе стороны каждой сделки, поиск по тому,
 * что человек называет в разговоре. Обычные «Рейсы» показывают свои сделки —
 * туда это складывать нельзя, иначе у поддержки и у перевозчика окажется
 * один и тот же список с разным содержимым.
 *
 * Сортировка — по давности последнего события: сюда приходят искать
 * застрявшее, а не любоваться на исправно едущие рейсы.
 */

const money = (v: number) => `${Math.round(v).toLocaleString('ru-RU')} ₽`;

/** «3 дня» вместо «76 ч»: часами дольше суток никто не меряет. */
function idle(hours: number): string {
  if (hours < 1) return 'только что';
  if (hours < 24) return `${hours} ч`;
  const d = Math.floor(hours / 24);
  return `${d} дн`;
}

/** Порог, после которого молчание в рейсе перестаёт быть нормой. */
function idleTone(hours: number, status: OrderStatus): 'ok' | 'warn' | 'bad' {
  // В пути сутки тишины — обычное дело: водитель едет, а не отчитывается.
  const limit = status === 'IN_TRANSIT' ? 48 : 12;
  if (hours >= limit * 2) return 'bad';
  if (hours >= limit) return 'warn';
  return 'ok';
}

interface Props {
  /** Ставки видит только владелец площадки — сервер их поддержке и не шлёт. */
  isAdmin: boolean;
  onOpenCargo: (id: string) => void;
}

type PendingCargo = CargoListItem & {
  comment: string | null;
  advanceNote: string | null;
  customsNote: string | null;
  contactPhone: string | null;
};

export default function Dispatch({ isAdmin, onOpenCargo }: Props) {
  const [board, setBoard] = useState<DispatchBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);

  /* Очередь на модерацию — только при включённой модерации заявок. */
  const [pending, setPending] = useState<PendingCargo[]>([]);
  const [modBusy, setModBusy] = useState<string | null>(null);

  async function loadModeration() {
    try {
      setPending((await api.cargoModeration()).items);
    } catch {
      setPending([]);
    }
  }
  useEffect(() => {
    void loadModeration();
  }, []);

  async function decide(id: string, decision: 'APPROVE' | 'REJECT') {
    let note: string | undefined;
    if (decision === 'REJECT') {
      note = window.prompt('Причина отказа (увидит автор):')?.trim() || undefined;
      if (!note) return;
    }
    setModBusy(id);
    try {
      await api.moderateCargo(id, decision, note);
      setPending((prev) => prev.filter((c) => c.id !== id));
      haptic();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setModBusy(null);
    }
  }

  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState<OrderStatus | ''>('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [stalled, setStalled] = useState(false);

  // Поиск с задержкой: по каждой букве дёргать базу на всех рейсах площадки
  // — верный способ положить её силами одного нетерпеливого сотрудника.
  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const query: DispatchQuery = useMemo(
    () => ({
      q: term || undefined,
      status: status || undefined,
      activeOnly: activeOnly && !status ? true : undefined,
      stalledHours: stalled ? 24 : undefined,
      limit: 30,
    }),
    [term, status, activeOnly, stalled],
  );

  const [cursor, setCursor] = useState<string | null>(null);

  async function load(append = false) {
    if (append) setMore(true);
    else setLoading(true);

    try {
      const r = await api.dispatch({
        ...query,
        ...(append && cursor ? { cursor } : {}),
      });

      setCursor(r.nextCursor);
      setBoard((prev) =>
        append && prev ? { ...r, items: [...prev.items, ...r.items] } : r,
      );
    } catch {
      // Молча: пустой список честнее, чем «что-то пошло не так».
    } finally {
      setLoading(false);
      setMore(false);
    }
  }

  useEffect(() => {
    setCursor(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Диспетчерская обязана быть свежей: она нужна ровно в тот момент,
  // когда на другом конце провода кто-то ждёт ответа.
  useRealtime('order:status', () => void load());
  useLiveRefresh(() => void load(), 30_000);

  const counts = board?.counts;
  // `useLiveRefresh` перечитывает доску раз в 30 секунд и на каждом
  // возврате фокуса — без этого признака уже показанная доска гасла бы
  // «Ищу…» на время каждого такого тихого обновления.
  const hasData = Boolean(board && board.items.length > 0);

  return (
    <>
      <h2>Диспетчерская</h2>
      <p className="meta">
        Все рейсы площадки. Поиск по номеру заявки, городу, имени или телефону
        любой из сторон, госномеру.
      </p>

      {pending.length > 0 && (
        <div className="card" style={{ marginTop: 'var(--s3)' }}>
          <h3>Модерация заявок ({pending.length})</h3>
          <p className="meta">
            Новые заявки ждут проверки, прежде чем попасть в ленту.
          </p>
          {pending.map((c) => (
            <div key={c.id} className="card" style={{ marginTop: 'var(--s2)' }}>
              <div className="route">
                {c.fromAddress} → {c.toAddress}
              </div>
              <div className="meta">
                {c.title} · {(c.weightKg / 1000).toFixed(1)} т ·{' '}
                {c.bodyTypes.map((b) => BODY_TYPE_LABEL[b]).join(', ')}
                {c.vehiclesCount > 1 ? ` · ${c.vehiclesCount} авто` : ''}
              </div>
              {c.comment && <div className="meta">{c.comment}</div>}
              {c.advanceNote && <div className="meta">Аванс: {c.advanceNote}</div>}
              {c.customsNote && <div className="meta">{c.customsNote}</div>}
              <div className="action-row" style={{ marginTop: 'var(--s2)' }}>
                <button
                  type="button"
                  className="secondary btn--compact"
                  onClick={() => { haptic(); onOpenCargo(c.id); }}
                >
                  Открыть
                </button>
                <button
                  type="button"
                  className="secondary btn--compact"
                  disabled={modBusy === c.id}
                  onClick={() => void decide(c.id, 'APPROVE')}
                >
                  Пропустить
                </button>
                <button
                  type="button"
                  className="danger btn--compact"
                  disabled={modBusy === c.id}
                  onClick={() => void decide(c.id, 'REJECT')}
                >
                  Отклонить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="searchbar" style={{ marginTop: 'var(--s3)' }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Номер, город, имя, телефон, госномер"
          aria-label="Поиск по рейсам"
        />
      </div>

      <div className="chips" style={{ marginTop: 'var(--s3)' }}>
        <button
          className={activeOnly && !status ? 'chip chip--on' : 'chip'}
          onClick={() => {
            haptic();
            setStatus('');
            setActiveOnly(true);
          }}
        >
          В работе
        </button>

        {ORDER_STATUSES.map((s) => (
          <button
            key={s}
            className={status === s ? 'chip chip--on' : 'chip'}
            onClick={() => {
              haptic();
              setStatus(status === s ? '' : s);
            }}
          >
            {ORDER_STATUS_LABEL[s]}
            {counts && counts[s] > 0 ? ` · ${counts[s]}` : ''}
          </button>
        ))}

        {/* Отдельный чип, а не сортировка: «застряло» — это то, ради чего
            экран открывают, и оно должно нажиматься одним движением. */}
        <button
          className={stalled ? 'chip chip--applied' : 'chip chip--ghost'}
          onClick={() => {
            haptic();
            setStalled((v) => !v);
          }}
        >
          <Icon name="clock" size={14} />
          Молчат сутки
          {board && board.stalled > 0 ? ` · ${board.stalled}` : ''}
        </button>
      </div>

      {loading && !hasData && (
        <div className="meta" style={{ padding: 'var(--s4)' }}>Ищу…</div>
      )}

      {!loading && board && board.items.length === 0 && (
        <div className="empty">
          Ничего не нашлось.
          <br />
          Проверьте написание или снимите фильтры.
        </div>
      )}

      {(!loading || hasData) &&
        board?.items.map((o: DispatchRow) => (
          <div key={o.orderId} className="card dispatch">
            <div className="dispatch__head">
              <button
                className="chip chip--ghost cargo__no"
                onClick={() => onOpenCargo(o.cargoId)}
                title="Открыть заявку"
              >
                № {o.number}
              </button>
              <span className="badge">{ORDER_STATUS_LABEL[o.status]}</span>
              <span
                className={`badge badge--${idleTone(o.idleHours, o.status)}`}
                data-tip="Сколько времени по рейсу ничего не менялось"
              >
                {idle(o.idleHours)}
              </span>
              {isAdmin && o.priceRub !== null && (
                <span className="price">{money(o.priceRub)}</span>
              )}
            </div>

            <div className="dispatch__route">
              <strong>{o.title}</strong>
              <span className="meta">
                {o.route}
                {o.distanceKm ? ` · ${o.distanceKm} км` : ''} ·{' '}
                {(o.weightKg / 1000).toFixed(1)} т
              </span>
            </div>

            {/* Обе стороны рядом: первый вопрос поддержки — «кто с кем». */}
            <div className="dispatch__sides">
              <span className="side">
                <span className="side__role">Логист</span>
                <strong>{o.logist.name}</strong>
                {o.logist.phone && (
                  <a href={`tel:${o.logist.phone}`}>{o.logist.phone}</a>
                )}
              </span>
              <span className="side">
                <span className="side__role">Исполнитель</span>
                <strong>{o.driver.name}</strong>
                {o.driver.phone && (
                  <a href={`tel:${o.driver.phone}`}>{o.driver.phone}</a>
                )}
                {o.vehiclePlate && <span className="meta">{o.vehiclePlate}</span>}
              </span>
            </div>

            <div className="dispatch__foot meta">
              {o.company && <span>{o.company.name}</span>}
              <span data-tip="Файлов по рейсу">
                <Icon name="doc" size={13} /> {o.filesCount}
              </span>
              <span data-tip="Записей о расходах">
                <Icon name="chart" size={13} /> {o.expensesCount}
              </span>
              <span>
                погрузка {new Date(o.loadDateFrom).toLocaleDateString('ru-RU')}
              </span>
            </div>
          </div>
        ))}

      {!loading && cursor && (
        <button className="secondary" disabled={more} onClick={() => void load(true)}>
          {more ? 'Загружаю…' : 'Показать ещё'}
        </button>
      )}
    </>
  );
}
